import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useAnimationSettings } from "@/components/animation/AnimationSettings";
import { PALETTE } from "@/components/animation/MotionShared";

const DEFAULT_ITEMS = [
  "ORACLE LIVE",
  "STOCKS",
  "CRYPTO",
  "FOREX",
  "SPORTS",
  "HEALTHCARE MARKETS",
  "STARTUPS",
  "GRANTS",
  "GLOBAL SIGNALS",
  "LOGIX-2 ACTIVE",
];

/**
 * Premium animated chyron crawl — horizontally translating ticker that
 * respects reduced-motion (becomes a static line in that case) and honours
 * the user's chyronSpeed preference.
 */
export default function ChyronTicker({
  items,
  testid = "chyron-ticker",
  sticky = false,
  height = 30,
}) {
  const { enabled, prefs } = useAnimationSettings();
  const data = useMemo(() => (items && items.length ? items : DEFAULT_ITEMS), [items]);
  const speed = Math.max(0.4, Math.min(2, Number(prefs.chyronSpeed) || 1));
  const duration = 40 / speed;
  const text = data.map((s) => `·  ${s}`).join("  ");
  return (
    <div
      role="region"
      aria-label="CareLogix ORACLE live ticker"
      data-testid={testid}
      style={{
        ...S.shell,
        position: sticky ? "sticky" : "relative",
        bottom: sticky ? 0 : undefined,
        height,
      }}
    >
      <div style={S.fadeLeft} aria-hidden="true" />
      {enabled ? (
        <motion.div
          style={S.crawl}
          animate={{ x: ["0%", "-50%"] }}
          transition={{ ease: "linear", duration, repeat: Infinity }}
        >
          <span style={S.text}>{text}  {text}</span>
        </motion.div>
      ) : (
        <div style={S.crawl}><span style={S.text}>{text}</span></div>
      )}
      <div style={S.fadeRight} aria-hidden="true" />
      <span style={S.live} aria-hidden="true">● LIVE</span>
    </div>
  );
}

const S = {
  shell: { width: "100%", overflow: "hidden", background: "linear-gradient(90deg, #040608 0%, #0e3a2c 50%, #040608 100%)", borderTop: `1px solid ${PALETTE.goldGlow}`, borderBottom: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.goldBright, display: "flex", alignItems: "center", boxSizing: "border-box", zIndex: 20, fontFamily: "'Space Mono', monospace" },
  crawl: { whiteSpace: "nowrap", display: "inline-block", paddingInlineStart: 24, willChange: "transform" },
  text: { color: PALETTE.goldBright, fontSize: 12, letterSpacing: "0.16em", fontWeight: 700 },
  fadeLeft: { pointerEvents: "none", position: "absolute", left: 0, top: 0, bottom: 0, width: 60, background: "linear-gradient(90deg, #040608, transparent)", zIndex: 1 },
  fadeRight: { pointerEvents: "none", position: "absolute", right: 50, top: 0, bottom: 0, width: 60, background: "linear-gradient(270deg, #040608, transparent)", zIndex: 1 },
  live: { position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: "#ef4444", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.18em", fontWeight: 800, padding: "3px 8px", border: "1px solid #ef4444", borderRadius: 6, background: "rgba(239,68,68,0.10)", zIndex: 2 },
};
